/**
 * Suppliers List Page - Manage supplier database
 * 
 * Connected to backend API:
 * - GET /api/v1/supplier-portal/suppliers
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Building2,
  Plus,
  Search,
  Filter,
  Globe,
  ChevronRight,
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  RefreshCw,
  AlertCircle,
  Users,
  DollarSign,
  TrendingUp,
  MoreVertical,
  Eye
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
import { apiFetch } from '../../../services/api_service';

export default function SuppliersListPage() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [tierFilter, setTierFilter] = useState('all');

  useEffect(() => {
    fetchSuppliers();
  }, [tierFilter, pagination.page]);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let url = `/supplier-portal/suppliers?page=${pagination.page}&limit=${pagination.limit}`;
      if (tierFilter !== 'all') {
        url += `&tier=${tierFilter}`;
      }
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }

      const data = await apiFetch(url);
      if (data?._error) throw new Error(data.message || 'Failed to fetch suppliers');
      setSuppliers(data.suppliers || []);
      setPagination(data.pagination || { page: 1, limit: 50, total: 0, pages: 0 });
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Stats
  const tierCounts = suppliers.reduce((acc, s) => {
    const tier = s.tier || 'unknown';
    acc[tier] = (acc[tier] || 0) + 1;
    return acc;
  }, {});

  // Filter by search locally
  const filteredSuppliers = suppliers.filter(s =>
    s.supplierName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.supplierId?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="suppliers-list-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 text-[#6B6B6B] text-sm mb-2">
            <Link to="/value-chain/supplier-portal" className="hover:text-purple-400 flex items-center gap-1">
              <ArrowLeft size={14} />
              <Globe size={14} />
              <span>Supplier Portal</span>
            </Link>
            <ChevronRight size={14} />
            <span className="text-white">Suppliers</span>
          </div>
          <h1 className="text-2xl font-bold text-white">Supplier Database</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Manage your supplier relationships
          </p>
        </div>
        
        <button
          onClick={() => navigate('/value-chain/supplier-portal/suppliers/new')}
          className="flex items-center gap-2 px-4 py-2.5 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 text-purple-400 rounded-lg transition-colors font-medium"
          data-testid="add-supplier-btn"
        >
          <Plus size={18} />
          Add Supplier
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard 
          label="Total Suppliers" 
          value={pagination.total} 
          icon={Building2}
          color="purple"
        />
        <StatCard 
          label="Tier 1" 
          value={tierCounts['1'] || 0}
          icon={TrendingUp}
          color="emerald"
        />
        <StatCard 
          label="Tier 2" 
          value={tierCounts['2'] || 0}
          icon={Users}
          color="blue"
        />
        <StatCard 
          label="Tier 3+" 
          value={(tierCounts['3'] || 0) + (tierCounts['unknown'] || 0)}
          icon={Globe}
          color="slate"
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
          <input
            type="text"
            placeholder="Search suppliers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white placeholder:text-[#6B6B6B] focus:border-purple-500/50 focus:outline-none"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-[#6B6B6B]" />
          <select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            className="px-3 py-2.5 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-purple-500/50 focus:outline-none cursor-pointer"
          >
            <option value="all">All Tiers</option>
            <option value="1">Tier 1</option>
            <option value="2">Tier 2</option>
            <option value="3">Tier 3</option>
          </select>
        </div>

        <button 
          onClick={fetchSuppliers}
          className="p-2.5 bg-[#111111] border border-[#1F1F1F] rounded-lg text-[#6B6B6B] hover:text-white hover:border-purple-500/30 transition-colors"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3">
          <AlertCircle className="text-red-400 shrink-0" size={20} />
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw size={24} className="text-purple-400 animate-spin" />
        </div>
      )}

      {/* Suppliers Grid */}
      {!loading && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSuppliers.map(supplier => (
            <SupplierCard key={supplier.id} supplier={supplier} />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredSuppliers.length === 0 && (
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-12 text-center">
          <Building2 className="mx-auto text-[#2F2F2F] mb-4" size={48} />
          <h3 className="text-lg font-semibold text-white mb-2">No suppliers found</h3>
          <p className="text-sm text-[#6B6B6B] mb-6">
            {searchQuery || tierFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'Add your first supplier to get started'}
          </p>
          {!searchQuery && tierFilter === 'all' && (
            <button 
              onClick={() => navigate('/value-chain/supplier-portal/suppliers/new')}
              className="px-5 py-2.5 rounded-lg bg-purple-500/20 border border-purple-500/30 text-purple-400 font-medium hover:bg-purple-500/30 transition-colors"
            >
              Add Supplier
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#6B6B6B]">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
          </p>
          <div className="flex items-center gap-2">
            <button 
              disabled={pagination.page === 1}
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              className="px-3 py-1.5 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-[#A0A0A0] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-[#6B6B6B]">
              Page {pagination.page} of {pagination.pages}
            </span>
            <button 
              disabled={pagination.page === pagination.pages}
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              className="px-3 py-1.5 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-[#A0A0A0] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Sub-components
function StatCard({ label, value, icon: Icon, color }) {
  const colorClasses = {
    purple: 'bg-purple-500/20 text-purple-400',
    emerald: 'bg-emerald-500/20 text-emerald-400',
    blue: 'bg-blue-500/20 text-blue-400',
    slate: 'bg-[#1F1F1F] text-[#6B6B6B]'
  };

  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon size={18} />
        </div>
        <div>
          <div className="text-2xl font-bold text-white">{value}</div>
          <div className="text-xs text-[#6B6B6B]">{label}</div>
        </div>
      </div>
    </div>
  );
}

function SupplierCard({ supplier }) {
  const navigate = useNavigate();

  return (
    <div 
      className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5 hover:border-purple-500/30 transition-all cursor-pointer"
      onClick={() => navigate(`/value-chain/supplier-portal/suppliers/${supplier.id}`)}
      data-testid={`supplier-card-${supplier.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <Building2 className="text-purple-400" size={20} />
          </div>
          <div>
            <h3 className="text-sm font-medium text-white">{supplier.supplierName}</h3>
            <p className="text-xs text-[#6B6B6B] font-mono">{supplier.supplierId}</p>
          </div>
        </div>
        {supplier.tier && (
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
            supplier.tier === '1' ? 'bg-emerald-500/20 text-emerald-400' :
            supplier.tier === '2' ? 'bg-blue-500/20 text-blue-400' :
            'bg-[#1F1F1F] text-[#6B6B6B]'
          }`}>
            Tier {supplier.tier}
          </span>
        )}
      </div>

      {/* Contact Info */}
      {supplier.primaryContact && (
        <div className="space-y-2 mb-4">
          {supplier.primaryContact.email && (
            <div className="flex items-center gap-2 text-xs text-[#A0A0A0]">
              <Mail size={12} className="text-[#6B6B6B]" />
              <span className="truncate">{supplier.primaryContact.email}</span>
            </div>
          )}
          {supplier.location?.country && (
            <div className="flex items-center gap-2 text-xs text-[#A0A0A0]">
              <MapPin size={12} className="text-[#6B6B6B]" />
              <span>{supplier.location.country}</span>
            </div>
          )}
        </div>
      )}

      {/* Category/Industry */}
      {supplier.category && (
        <span className="inline-block px-2 py-0.5 rounded bg-[#1F1F1F] text-[10px] text-[#6B6B6B]">
          {supplier.category}
        </span>
      )}

      {/* Business Metrics */}
      {supplier.businessMetrics?.annualSpend2024 && (
        <div className="mt-4 pt-4 border-t border-[#1F1F1F]">
          <div className="flex items-center justify-between text-xs">
            <span className="text-[#6B6B6B]">Annual Spend</span>
            <span className="text-white font-medium">
              ${(supplier.businessMetrics.annualSpend2024 / 1000000).toFixed(1)}M
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
